import { useCallback } from 'react';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { useEmployeeSelectSearch } from '@/hooks/useEmployeeSelectSearch';
import type { HrOrgFilterState } from '@/modules/hr/components/hrEmployeeFilterUtils';
import { formatEmployeeShort } from '@/utils/entityLabels';
import { useSelectLabels } from '@/hooks/useSelectLabels';

export interface EmployeeMultiSelectProps {
  label?: string;
  values?: string[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  error?: string;
  hrScoped?: boolean;
  orgFilters?: HrOrgFilterState;
}

/** Searchable multi-employee picker with optional org-structure filters. */
export function EmployeeMultiSelect({
  label = 'Employees',
  values = [],
  onChange,
  placeholder,
  disabled,
  className,
  required,
  error,
  hrScoped = true,
  orgFilters,
}: EmployeeMultiSelectProps) {
  const selectLabels = useSelectLabels();
  const { options, loading, searching, onSearchQuery, getEmployee } = useEmployeeSelectSearch({
    value: values[0] ?? '',
    hrScoped,
    pageSize: 50,
    orgFilters,
  });

  const getOptionLabel = useCallback(
    (value: string) => {
      const employee = getEmployee(value);
      return employee ? formatEmployeeShort(employee) : undefined;
    },
    [getEmployee],
  );

  return (
    <SearchableMultiSelect
      label={label}
      values={values}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? selectLabels.selectEmployees}
      disabled={disabled}
      loading={loading}
      searching={searching}
      className={className}
      required={required}
      error={error}
      filterLocally={false}
      onSearchQuery={onSearchQuery}
      getOptionLabel={getOptionLabel}
      searchPlaceholder={selectLabels.searchEmployee}
      emptyMessage={selectLabels.noEmployees}
      allowClear
    />
  );
}
